import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { apiFetch, ApiError } from "@/lib/api";
import { NanaeHeader } from "@/components/qv3/NanaeShared";
import { useAuth } from "@/contexts/AuthContext";
import { MaieuteChat } from "@/components/qorum/MaieuteChat";
import {
  DebateQuotaDialog,
  extractQuotaPayload,
  type DebateQuotaPayload,
} from "@/components/qv3/DebateQuotaDialog";
import { TENDANCES_ENABLED, AUDIT_ENABLED, VERIF_ENABLED, isTendancesMode, isAuditMode } from "@/lib/branches";
import { socialMeta } from "@/lib/seo-meta";


type AttachmentSearch = {
  url: string;
  name: string;
  size: number;
  mimeType: string;
};

type AttachmentManifestItem = {
  storage_path: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
};

type Search = {
  question?: string;
  mode?: string;
  branch?: "business" | "tendances" | "audit" | "verif";
  tendances_mode?: string;
  audit_mode?: string;
  attachments?: AttachmentSearch[];
  manifest?: AttachmentManifestItem[];
  expert_ids?: string[];
};

function parseAttachments(v: unknown): AttachmentSearch[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: AttachmentSearch[] = [];
  for (const item of v) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (typeof o.url !== "string" || typeof o.name !== "string") continue;
    out.push({
      url: o.url,
      name: o.name,
      size: typeof o.size === "number" ? o.size : 0,
      mimeType: typeof o.mimeType === "string" ? o.mimeType : "application/octet-stream",
    });
  }
  return out.length > 0 ? out : undefined;
}

function parseManifest(v: unknown): AttachmentManifestItem[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: AttachmentManifestItem[] = [];
  for (const item of v) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (typeof o.storage_path !== "string" || typeof o.filename !== "string") continue;
    out.push({
      storage_path: o.storage_path,
      filename: o.filename,
      mime_type: typeof o.mime_type === "string" ? o.mime_type : "application/octet-stream",
      size_bytes: typeof o.size_bytes === "number" ? o.size_bytes : 0,
    });
  }
  return out.length > 0 ? out.slice(0, 5) : undefined;
}

function parseExpertIds(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === "string" && x.length > 0);
  return out.length > 0 ? out : undefined;
}

export const Route = createFileRoute("/antichambre")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    question: typeof s.question === "string" ? s.question : undefined,
    mode: typeof s.mode === "string" ? s.mode : undefined,
    branch:
      s.branch === "tendances" || s.branch === "business" || s.branch === "audit" || s.branch === "verif"
        ? s.branch
        : undefined,
    tendances_mode:
      typeof s.tendances_mode === "string" ? s.tendances_mode : undefined,
    audit_mode:
      typeof s.audit_mode === "string" ? s.audit_mode : undefined,
    attachments: parseAttachments(s.attachments),
    manifest: parseManifest(s.manifest),
    expert_ids: parseExpertIds(s.expert_ids),
  }),
  head: () => ({
    meta: [
      { title: "Convocation du Conseil — Hōjin" },
      { name: "description", content: "L'Antichambre — Émile vous prépare avant de convoquer le Conseil sur votre saisine." },
      { name: "robots", content: "noindex" },
      ...socialMeta({
        title: "Convocation du Conseil — Hōjin",
        description: "L'Antichambre — Émile vous prépare avant de convoquer le Conseil sur votre saisine.",
        url: "https://hojin.ai/antichambre",
        image: "maieute",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/antichambre" }],
  }),
  component: AntichambrePage,
});

/**
 * Mode business — pass-through.
 * Les clés émises par BusinessModeSelector (cf. src/lib/branches.ts) sont
 * déjà les clés canoniques backend. On ne downgrade JAMAIS silencieusement
 * vers "auto" : seul un mode vide retombe sur le défaut "auto" (Général).
 */
function toBackendMode(mode: string): string {
  const trimmed = mode?.trim();
  return trimmed ? trimmed : "auto";
}

type RejectedAttachment = {
  filename?: string;
  reason?: string;
};

type ChatCreateResponse = {
  conversation_id?: string;
  conversationId?: string;
  id?: string;
  maieutique_session_id?: string;
  session_id?: string;
  question?: string;
  message?: string;
  attachments_accepted?: number;
  attachments_rejected?: RejectedAttachment[];
};

export function AntichambrePage() {
  const search = Route.useSearch();
  return (
    <ProtectedRoute>
      <AntichambreContent
        initialQuestion={search.question ?? ""}
        initialMode={search.mode ?? ""}
        initialBranch={search.branch ?? "business"}
        initialTendancesMode={search.tendances_mode ?? ""}
        initialAuditMode={search.audit_mode ?? ""}
        initialAttachments={search.attachments ?? []}
        initialManifest={search.manifest ?? []}
        initialExpertIds={search.expert_ids ?? []}
      />
    </ProtectedRoute>
  );
}

export function AntichambreContent({
  initialQuestion,
  initialMode,
  initialBranch = "business",
  initialTendancesMode = "",
  initialAuditMode = "",
  initialAttachments = [],
  initialManifest = [],
  initialExpertIds: _initialExpertIds = [],
}: {
  initialQuestion: string;
  initialMode: string;
  initialBranch?: "business" | "tendances" | "audit" | "verif";
  initialTendancesMode?: string;
  initialAuditMode?: string;
  initialAttachments?: AttachmentSearch[];
  initialManifest?: AttachmentManifestItem[];
  initialExpertIds?: string[];
}) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<DebateQuotaPayload | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [imaniFirst, setImaniFirst] = useState<string>("");
  const firedRef = useRef(false);

  useEffect(() => {
    const prevBg = document.body.style.background;
    const prevColor = document.body.style.color;
    document.body.style.background = "#ffffff";
    document.body.style.color = "#0a0a0a";
    return () => {
      document.body.style.background = prevBg;
      document.body.style.color = prevColor;
    };
  }, []);

  const handleApiError = (e: unknown) => {
    const quotaPayload = extractQuotaPayload(e);
    if (quotaPayload) {
      setQuota(quotaPayload);
      firedRef.current = false;
      return;
    }
    if (e instanceof ApiError && e.status === 401) {
      setError("Votre session a expiré. Reconnectez-vous pour saisir le Conseil.");
    } else if (e instanceof ApiError && e.status >= 500) {
      setError("Erreur serveur. Veuillez réessayer.");
    } else if (e instanceof TypeError) {
      setError("Connexion perdue. Vérifiez votre réseau.");
    } else {
      setError("Le Conseil n'a pas pu être convoqué. Veuillez réessayer.");
    }
  };

  useEffect(() => {
    if (firedRef.current) return;
    if (!initialQuestion.trim()) {
      setError("Aucune question fournie. Retournez au Bureau pour saisir le Conseil.");
      return;
    }
    firedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const isTendances =
          initialBranch === "tendances" &&
          TENDANCES_ENABLED &&
          isTendancesMode(initialTendancesMode);
        const isAudit =
          initialBranch === "audit" &&
          AUDIT_ENABLED &&
          isAuditMode(initialAuditMode);
        const isVerif = initialBranch === "verif" && VERIF_ENABLED;

        const body: Record<string, unknown> = isVerif
          ? {
              question: initialQuestion,
              branch: "verif",
              mode: "verif",
              modelTier: "mid",
            }
          : isAudit
            ? {
                question: initialQuestion,
                branch: "audit",
                mode: initialAuditMode,
              }
            : isTendances
              ? {
                  question: initialQuestion,
                  branch: "tendances",
                  mode: initialTendancesMode,
                }
              : {
                  question: initialQuestion,
                  mode: toBackendMode(initialMode),
                };
        if (initialManifest.length > 0) {
          body.attachments = initialManifest.slice(0, 5);
        }
        const res = await apiFetch<ChatCreateResponse>("/api/maieutique/start", {
          method: "POST",
          body: JSON.stringify(body),
        });
        const convId =
          res?.conversation_id ?? res?.conversationId ?? res?.id ?? null;
        const sessId =
          res?.maieutique_session_id ?? res?.session_id ?? null;
        if (!convId || !sessId) throw new Error("session manquante");

        if (Array.isArray(res?.attachments_rejected) && res.attachments_rejected.length > 0) {
          const reasonLabel: Record<string, string> = {
            forbidden_path: "chemin invalide",
            mime_blocked: "format refusé",
            size_too_large: "trop volumineux",
            storage_not_found: "fichier introuvable",
          };
          const summary = res.attachments_rejected
            .map((r) => `${r.filename ?? "fichier"} (${reasonLabel[r.reason ?? ""] ?? r.reason ?? "rejeté"})`)
            .join(", ");
          // eslint-disable-next-line no-console
          console.warn("[chat] attachments_rejected:", summary);
        }

        // Les anciennes références d'URL ne sont pas renvoyées sur la route
        // multipart : cette route exige un vrai champ `file`. Les nouveaux
        // dépôts passent par le bouton + et le manifeste reste le chemin
        // d'ouverture officiel de la saisine.
        if (cancelled) return;
        setImaniFirst((res?.question ?? res?.message ?? "").trim());
        setSessionId(sessId);
        setConversationId(convId);
      } catch (e) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("[chat] POST /api/chat failed", e);
        handleApiError(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initialQuestion, initialMode, initialBranch, initialTendancesMode, initialAuditMode, initialAttachments, initialManifest]);

  const retry = () => {
    firedRef.current = false;
    setError(null);
    navigate({
      to: "/antichambre",
      search: {
        question: initialQuestion,
        mode: initialMode || undefined,
        attachments: initialAttachments.length > 0 ? initialAttachments : undefined,
        manifest: initialManifest.length > 0 ? initialManifest : undefined,
      },
      replace: true,
    });
  };

  const { user } = useAuth();

  return (
    <div style={{ background: "#ffffff", color: "#0a0a0a", minHeight: "100vh" }}>
      <NanaeHeader
        variant="app"
        user={{
          email: user?.email,
          name:
            (user?.user_metadata as { first_name?: string } | undefined)?.first_name ??
            user?.email,
        }}
      />

      {error ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 80px)",
            gap: 24,
            textAlign: "center",
            padding: "48px 24px",
          }}
        >
          <p
            role="alert"
            data-testid="qa-error"
            style={{ color: "#FF1744", maxWidth: 480, fontFamily: '"DM Sans", sans-serif' }}
          >
            {error}
          </p>
          <button
            type="button"
            onClick={retry}
            style={{
              fontFamily: "Oswald, sans-serif",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: 3,
              textTransform: "uppercase",
              padding: "14px 22px",
              background: "#0a0a0a",
              color: "#ffffff",
              border: "0.5px solid #0a0a0a",
              cursor: "pointer",
            }}
          >
            Réessayer
          </button>
        </div>
      ) : conversationId && sessionId ? (
        (() => {
          const isTendances =
            initialBranch === "tendances" &&
            TENDANCES_ENABLED &&
            isTendancesMode(initialTendancesMode);
          const isAudit =
            initialBranch === "audit" &&
            AUDIT_ENABLED &&
            isAuditMode(initialAuditMode);
          const isVerif = initialBranch === "verif" && VERIF_ENABLED;
          const mode = isVerif
            ? "verif"
            : isAudit
              ? initialAuditMode
              : isTendances
                ? initialTendancesMode
                : toBackendMode(initialMode);
          const branch: "business" | "tendances" | "audit" | "verif" = isVerif
            ? "verif"
            : isAudit
              ? "audit"
              : isTendances
                ? "tendances"
                : "business";
          return (
            <MaieuteChat
              sessionId={sessionId}
              initialQuestion={initialQuestion}
              initialImaniMessage={imaniFirst}
              initialAttachmentCount={Math.max(initialManifest.length, initialAttachments.length)}
              mode={mode}
              branch={branch}
              onConvened={() =>
                navigate({ to: "/deliberation/$id", params: { id: conversationId }, search: { branch } })
              }
              onError={handleApiError}
            />
          );
        })()
      ) : (
        <div
          role="status"
          aria-live="polite"
          aria-label="Ouverture de l'antichambre"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 80px)",
            gap: 24,
            textAlign: "center",
            padding: "48px 24px",
          }}
        >
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: "#FF1744",
              opacity: 0.85,
              animation: "qa-pulse 1.2s ease-in-out infinite",
            }}
          />
          <p
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontStyle: "italic",
              fontSize: 18,
              color: "#FF1744",
              maxWidth: 560,
              margin: 0,
            }}
          >
            Imani vous accueille…
          </p>
        </div>
      )}

      <DebateQuotaDialog
        payload={quota}
        onClose={() => {
          setQuota(null);
          navigate({ to: "/saisine", replace: true });
        }}
      />
    </div>
  );
}
